import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { 
  Store, 
  Users, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Eye, 
  Edit, 
  Trash2, 
  Plus,
  Package,
  TrendingUp,
  AlertTriangle,
  FileText,
  Phone,
  Mail,
  MapPin,
  Calendar,
  DollarSign,
  StarIcon,
  MessageSquare,
  Building2,
  UserCheck,
  Ban,
  ShoppingCart,
  TestTube,
  Globe,
  Shield,
  Wallet
} from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { usePersistentTabState } from '@/hooks/usePersistentTabState';
import { useLanguage } from "@/hooks/useLanguage";
import { useTranslations } from "@/hooks/useTranslations";
import { AddVendorForm } from '@/components/admin/AddVendorForm';
import { VendorManagersTab } from '@/components/admin/VendorManagersTab';
import AdminVendorFinances from '@/components/admin/AdminVendorFinances';
import { AdminWithdrawalManager } from '@/components/admin/AdminWithdrawalManager';
import { AdminCommissionWalletManager } from '@/components/admin/AdminCommissionWalletManager';
import VendorFinanceDetails from '@/components/admin/VendorFinanceDetails';
import { formatPrice, formatPriceWithCurrency } from '@/utils/currency';
import { VendorFilters } from './VendorFilters';
import { useCurrency } from '@/hooks/useCurrency';
import { ProductImageUpload } from '@/components/products/ProductImageUpload';
import WysiwygEditor from '@/components/WysiwygEditor';

interface VendorsManagerProps {
  onRefresh: () => void;
  user: any;
}

interface VendorApplication {
  id: string;
  user_id: string;
  business_name: string;
  business_description: string | null;
  business_address: string;
  business_phone: string;
  business_email: string;
  business_type: string;
  business_entity_type?: string;
  experience: string | null;
  motivation: string | null;
  status: string;
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  identity_document_url?: string | null;
  profile?: {
    full_name: string;
    business_name: string;
    phone: string;
    avatar_url: string;
    email: string;
  };
}

interface Vendor {
  id: string;
  full_name: string;
  display_name?: string;
  business_name?: string | null;
  business_address?: string | null;
  business_email?: string | null;
  phone: string | null;
  avatar_url: string | null;
  vendor_status: string;
  created_at: string;
  products_count?: number;
  total_revenue?: number;
  total_revenue_currency?: string;
  orders_count?: number;
  rating?: number;
  role?: string;
  is_internal?: boolean;
  business_hours?: any;
  employee?: {
    first_name: string;
    last_name: string;
    position: string;
  };
}

interface Product {
  id: string;
  title: string;
  description?: string | null;
  price_cents: number;
  currency: string;
  stock: number;
  images?: string[];
  category?: string | null;
  brand?: string | null;
  active: boolean;
  created_at: string;
  vendor?: { full_name: string; business_name: string | null; email?: string };
}

export const VendorsManager = ({ onRefresh, user }: VendorsManagerProps) => {
  const { toast } = useToast();
  const { t: tDb } = useTranslations();
  const { displayCurrency, convertPrice } = useCurrency();
  const [activeTab, setActiveTab] = usePersistentTabState(
    'admin-vendors-manager:activeTab',
    'applications',
    ['applications', 'vendors', 'finances', 'products', 'analytics', 'managers']
  );
  const [financeTab, setFinanceTab] = usePersistentTabState(
    'admin-vendors-manager:financeTab',
    'overview',
    ['overview', 'withdrawals', 'commissions']
  );
  const [vendorDetailsTab, setVendorDetailsTab] = usePersistentTabState(
    'admin-vendors-manager:vendorDetailsTab',
    'info',
    ['info', 'finances', 'stats']
  );
  
  // State
  const [applications, setApplications] = useState<VendorApplication[]>([]);
  const [filteredApplications, setFilteredApplications] = useState<VendorApplication[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [filteredVendors, setFilteredVendors] = useState<Vendor[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states for applications
  const [appStatusFilter, setAppStatusFilter] = useState('all');
  const [appSearchQuery, setAppSearchQuery] = useState('');
  const [appSortBy, setAppSortBy] = useState('created_at');
  const [appSortOrder, setAppSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Filter states for vendors
  const [vendorStatusFilter, setVendorStatusFilter] = useState('all');
  const [vendorSearchQuery, setVendorSearchQuery] = useState('');
  const [vendorSortBy, setVendorSortBy] = useState('created_at');
  const [vendorSortOrder, setVendorSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Dialog states
  const [applicationDialog, setApplicationDialog] = useState(false);
  const [vendorDialog, setVendorDialog] = useState(false);
  const [addVendorDialog, setAddVendorDialog] = useState(false);
  const [productDialog, setProductDialog] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<VendorApplication | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  
  // Review form
  const [reviewForm, setReviewForm] = useState({
    status: '',
    admin_notes: ''
  });

  // Edit vendor form
  const [editVendorForm, setEditVendorForm] = useState({
    full_name: '',
    business_name: '',
    phone: '',
    business_address: '',
    business_email: '',
    is_internal: false,
    stock_test_mode: null as boolean | null,
    business_hours: {
      monday: { open: '09:00', close: '18:00', closed: false },
      tuesday: { open: '09:00', close: '18:00', closed: false },
      wednesday: { open: '09:00', close: '18:00', closed: false },
      thursday: { open: '09:00', close: '18:00', closed: false },
      friday: { open: '09:00', close: '18:00', closed: false },
      saturday: { open: '09:00', close: '16:00', closed: false },
      sunday: { open: '10:00', close: '16:00', closed: false }
    }
  });

  // Edit product form
  const [productForm, setProductForm] = useState({
    title: '',
    description: '',
    price_cents: 0,
    currency: 'CDF',
    stock: 0,
    images: [] as string[],
    category: '',
    brand: '',
    active: true,
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchApplications(),
        fetchVendors(),
        fetchProducts()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      console.log('🔍 Fetching vendor applications...');
      
      const { data, error } = await supabase
        .from('vendor_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching applications:', error);
        throw error;
      }
      
      console.log('✅ Raw applications fetched:', data?.length || 0, 'applications');
      console.log('📋 Applications data:', data);
      
      // Fetch profile data separately for each application
      if (data && data.length > 0) {
        const applicationsWithProfiles = await Promise.all(
          data.map(async (app) => {
            console.log(`🔍 Fetching profile for user ${app.user_id}`);
            const { data: profileData, error: profileError } = await supabase
              .from('profiles')
              .select('full_name, business_name, phone, avatar_url, email')
              .eq('id', app.user_id)
              .single();
            
            if (profileError) {
              console.warn(`⚠️ Profile error for ${app.user_id}:`, profileError);
            }
            
            console.log(`✅ Profile found for ${app.user_id}:`, profileData);
            
            return {
              ...app,
              profile: profileData
            };
          })
        );
        
        console.log('✅ Applications with profiles:', applicationsWithProfiles.length);
        console.log('📊 Final data:', applicationsWithProfiles);
        setApplications(applicationsWithProfiles);
      } else {
        console.log('⚠️ No applications found in database');
        setApplications([]);
      }
    } catch (error) {
      console.error('❌ Error fetching applications:', error);
      toast({
        title: "Erreur de chargement",
        description: "Impossible de charger les candidatures. Vérifiez la console.",
        variant: "destructive",
      });
    }
  };

  const fetchVendors = async () => {
    try {
      const { data: vendorsData, error: vendorsError } = await supabase
        .from('profiles')
        .select(`
          id,
          full_name,
          business_name,
          business_address,
          business_email,
          phone,
          avatar_url,
          vendor_status,
          created_at,
          system_role,
          is_internal,
          business_hours,
          stock_test_mode,
          employee:employees!profiles_employee_id_fkey(first_name, last_name, position)
        `)
        .eq('vendor_status', 'approved')
        .order('created_at', { ascending: false });

      if (vendorsError) throw vendorsError;

      // Fetch additional vendor stats
      const vendorsWithStats = await Promise.all(
        (vendorsData || []).map(async (vendor) => {
          const [productsRes, ordersRes] = await Promise.all([
            supabase.from('products').select('id').eq('vendor_id', vendor.id),
            supabase.from('orders').select('amount_cents, currency').eq('vendor_id', vendor.id)
          ]);

          const productsCount = productsRes.data?.length || 0;
          const orders = ordersRes.data || [];
          const totalRevenue = orders.reduce((sum, order) => sum + (order.amount_cents || 0), 0) / 100;
          const totalRevenueCurrency = orders.length > 0 ? orders[0].currency : 'CDF';

          // Récupérer les avis pour les produits du vendeur
          const productIds = productsRes.data?.map(p => p.id) || [];
          let avgRating = 0;
          
          if (productIds.length > 0) {
            const { data: reviewsData } = await supabase
              .from('product_reviews')
              .select('rating')
              .in('product_id', productIds);
            
            const reviews = reviewsData || [];
            avgRating = reviews.length > 0
              ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length
              : 0;
          }

          // Déterminer le nom d'affichage selon le type
          let displayName = vendor.full_name;
          if (vendor.is_internal && vendor.employee) {
            displayName = `${vendor.employee.first_name} ${vendor.employee.last_name}`;
          }

          return {
            ...vendor,
            display_name: displayName,
            products_count: productsCount,
            total_revenue: totalRevenue,
            total_revenue_currency: totalRevenueCurrency,
            orders_count: orders.length,
            rating: avgRating,
            is_internal: vendor.is_internal || false
          };
        })
      );

      setVendors(vendorsWithStats);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          vendor:profiles!vendor_id(full_name, business_name)
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleReviewApplication = async (application: VendorApplication, status: string, notes: string) => {
    try {
      // Update application status
      const { error: updateError } = await supabase
        .from('vendor_applications')
        .update({
          status,
          admin_notes: notes,
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', application.id);

      if (updateError) throw updateError;

      if (status === 'approved') {
        // Update user profile to vendor role
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            system_role: 'vendor',
            vendor_status: 'approved'
          })
          .eq('id', application.user_id);

        if (profileError) throw profileError;
        
        // Envoyer email d'approbation
        try {
          await supabase.functions.invoke('send-vendor-decision', {
            body: {
              vendorEmail: application.business_email || application.profile?.email,
              vendorName: application.profile?.full_name || 'Candidat',
              businessName: application.business_name,
              decision: 'approved',
              adminNotes: notes,
            }
          });
          console.log('✅ Email d\'approbation envoyé');
        } catch (emailError) {
          console.error('⚠️ Erreur envoi email (non bloquant):', emailError);
        }
        
        toast({ title: "Demande approuvée et utilisateur promu vendeur", description: "Email de confirmation envoyé" });
      } else {
        // Update vendor status to rejected
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ vendor_status: 'rejected' })
          .eq('id', application.user_id);

        if (profileError) throw profileError;
        
        // Envoyer email de refus
        try {
          await supabase.functions.invoke('send-vendor-decision', {
            body: {
              vendorEmail: application.business_email || application.profile?.email,
              vendorName: application.profile?.full_name || 'Candidat',
              businessName: application.business_name,
              decision: 'rejected',
              adminNotes: notes,
            }
          });
          console.log('✅ Email de refus envoyé');
        } catch (emailError) {
          console.error('⚠️ Erreur envoi email (non bloquant):', emailError);
        }
        
        toast({ title: "Demande rejetée", description: "Email de notification envoyé" });
      }

      await fetchAllData();
      onRefresh();
      setApplicationDialog(false);
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleDeleteApplication = async (application: VendorApplication) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette candidature ? Cette action est irréversible.')) {
      return;
    }

    try {
      // Supprimer la candidature
      const { error: deleteError } = await supabase
        .from('vendor_applications')
        .delete()
        .eq('id', application.id);

      if (deleteError) throw deleteError;

      // Réinitialiser le vendor_status du profil ET remettre le rôle à client
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ 
          vendor_status: null,
          system_role: 'client'
        })
        .eq('id', application.user_id);

      if (profileError) throw profileError;

      // Supprimer aussi le rôle vendor de user_roles s'il existe
      const { error: roleError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', application.user_id)
        .eq('role', 'vendor');

      // Ne pas throw d'erreur si pas de rôle trouvé
      if (roleError && !roleError.message.includes('No rows found')) {
        console.warn('Erreur suppression user_roles (non critique):', roleError);
      }

      // Envoyer email uniquement au super_admin
      try {
        await supabase.functions.invoke('send-super-admin-notification', {
          body: {
            action: 'vendor_application_deleted',
            candidateName: application.profile?.full_name || 'Inconnu',
            candidateEmail: application.business_email || application.profile?.email,
            businessName: application.business_name,
            deletedBy: user?.full_name || user?.email || 'Admin',
            deletedAt: new Date().toISOString(),
          }
        });
        console.log('✅ Email super_admin envoyé');
      } catch (emailError) {
        console.error('⚠️ Erreur email super_admin (non bloquant):', emailError);
      }

      toast({
        title: "Candidature supprimée",
        description: "La candidature a été supprimée avec succès. Le super administrateur a été notifié."
      });

      await fetchAllData();
      onRefresh();
      setApplicationDialog(false);
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleVendorStatusChange = async (vendorId: string, newStatus: 'pending' | 'approved' | 'rejected') => {
    try {
      // Déterminer le system_role selon le statut
      let system_role = 'client';
      if (newStatus === 'approved') {
        system_role = 'vendor';
      } else if (newStatus === 'pending' || newStatus === 'rejected') {
        system_role = 'client';
      }

      // Mettre à jour le vendor_status et system_role dans profiles
      const { error } = await supabase
        .from('profiles')
        .update({ 
          vendor_status: newStatus,
          system_role: system_role as any
        })
        .eq('id', vendorId);

      if (error) throw error;

      // Gérer les rôles dans user_roles selon le statut
      if (newStatus === 'approved') {
        // Ajouter le rôle vendor s'il n'existe pas
        const { error: roleError } = await supabase
          .from('user_roles')
          .upsert({
            user_id: vendorId,
            role: 'vendor'
          }, {
            onConflict: 'user_id,role'
          });
        
        if (roleError && !roleError.message.includes('duplicate')) {
          console.warn('Erreur ajout user_roles (non critique):', roleError);
        }
      } else if (newStatus === 'pending' || newStatus === 'rejected') {
        // Supprimer le rôle vendor s'il existe
        const { error: roleError } = await supabase
          .from('user_roles')
          .delete()
          .eq('user_id', vendorId)
          .eq('role', 'vendor');

        if (roleError && !roleError.message.includes('No rows found')) {
          console.warn('Erreur suppression user_roles (non critique):', roleError);
        }
      }
      
      // Mettre à jour l'application correspondante si elle existe
      const { error: appError } = await supabase
        .from('vendor_applications')
        .update({ status: newStatus })
        .eq('user_id', vendorId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (appError) {
        console.warn('Error updating application status:', appError);
      }
      
      toast({ title: `Statut vendeur mis à jour: ${newStatus}` });
      await fetchVendors();
      await fetchAllData(); // Rafraîchir aussi les candidatures
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleToggleVendorType = async (vendorId: string, makeInternal: boolean) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_internal: makeInternal })
        .eq('id', vendorId);

      if (error) throw error;
      
      toast({ 
        title: `Vendeur converti en ${makeInternal ? 'interne' : 'externe'}`,
        description: makeInternal ? 
          "Le vendeur est maintenant lié à un employé interne" : 
          "Le vendeur est maintenant un vendeur externe indépendant"
      });
      await fetchVendors();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleDeactivateVendor = async (vendorId: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          vendor_status: 'rejected',
          role: 'client'
        })
        .eq('id', vendorId);

      if (error) throw error;
      
      toast({ 
        title: "Vendeur désactivé",
        description: "Le vendeur a été désactivé et rétrogradé en client"
      });
      await fetchVendors();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleUpdateVendor = async () => {
    if (!selectedVendor) return;
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: editVendorForm.full_name,
          business_name: editVendorForm.business_name,
          phone: editVendorForm.phone,
          business_address: editVendorForm.business_address,
          business_email: editVendorForm.business_email,
          is_internal: editVendorForm.is_internal,
          stock_test_mode: editVendorForm.stock_test_mode,
          business_hours: editVendorForm.business_hours
        })
        .eq('id', selectedVendor.id);

      if (error) throw error;
      
      toast({ 
        title: "Vendeur mis à jour",
        description: "Les informations du vendeur ont été mises à jour avec succès"
      });
      await fetchVendors();
      setVendorDialog(false);
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleEditProduct = (product: Product) => {
    setSelectedProduct(product);
    setProductForm({
      title: product.title,
      description: product.description || '',
      price_cents: product.price_cents,
      currency: product.currency,
      stock: product.stock,
      images: product.images || [],
      category: product.category || '',
      brand: product.brand || '',
      active: product.active,
    });
    setProductDialog(true);
  };

  const handleSaveProduct = async () => {
    if (!selectedProduct) return;

    try {
      const { error } = await supabase
        .from('products')
        .update({
          title: productForm.title,
          description: productForm.description,
          price_cents: productForm.price_cents,
          currency: productForm.currency,
          stock: productForm.stock,
          images: productForm.images,
          category: productForm.category,
          brand: productForm.brand,
          active: productForm.active,
        })
        .eq('id', selectedProduct.id);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'Produit mis à jour avec succès',
      });

      setProductDialog(false);
      await fetchProducts();
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;

    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'Produit supprimé avec succès',
      });

      await fetchProducts();
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const initializeEditForm = (vendor: Vendor) => {
    setEditVendorForm({
      full_name: vendor.full_name || '',
      business_name: vendor.business_name || '',
      phone: vendor.phone || '',
      business_address: vendor.business_address || '',
      business_email: vendor.business_email || '',
      is_internal: vendor.is_internal || false,
      stock_test_mode: (vendor as any).stock_test_mode ?? null,
      business_hours: vendor.business_hours || {
        monday: { open: '09:00', close: '18:00', closed: false },
        tuesday: { open: '09:00', close: '18:00', closed: false },
        wednesday: { open: '09:00', close: '18:00', closed: false },
        thursday: { open: '09:00', close: '18:00', closed: false },
        friday: { open: '09:00', close: '18:00', closed: false },
        saturday: { open: '09:00', close: '16:00', closed: false },
        sunday: { open: '10:00', close: '16:00', closed: false }
      }
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="text-orange-600 border-orange-300"><Clock className="h-3 w-3 mr-1" />{tDb('pending', 'En attente')}</Badge>;
      case 'approved':
        return <Badge variant="outline" className="text-green-600 border-green-300"><CheckCircle className="h-3 w-3 mr-1" />{tDb('approved', 'Approuvé')}</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="text-red-600 border-red-300"><XCircle className="h-3 w-3 mr-1" />{tDb('rejected', 'Rejeté')}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            {tDb('admin.tabs.vendors', 'Gestion des Vendeurs')}
          </h2>
          <p className="text-muted-foreground mt-1">
            {tDb('vendor.manage_vendors', 'Gérez les demandes et le statut des vendeurs')}
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-6 bg-white/80 backdrop-blur-sm shadow-lg rounded-xl p-1">
          <TabsTrigger value="applications" className="flex items-center space-x-2">
            <FileText className="h-4 w-4" />
            <span>{tDb('applications', 'Candidatures')}</span>
          </TabsTrigger>
          <TabsTrigger value="vendors" className="flex items-center space-x-2">
            <Store className="h-4 w-4" />
            <span>{tDb('admin.tabs.vendors', 'Vendeurs')}</span>
          </TabsTrigger>
          <TabsTrigger value="finances" className="flex items-center space-x-2">
            <DollarSign className="h-4 w-4" />
            <span>Finances</span>
          </TabsTrigger>
          <TabsTrigger value="products" className="flex items-center space-x-2">
            <Package className="h-4 w-4" />
            <span>{tDb('products.title', 'Produits')}</span>
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center space-x-2">
            <TrendingUp className="h-4 w-4" />
            <span>{tDb('analytics.title', 'Analytiques')}</span>
          </TabsTrigger>
          <TabsTrigger value="managers" className="flex items-center space-x-2">
            <Users className="h-4 w-4" />
            <span>{tDb('managers', 'Gestionnaires')}</span>
          </TabsTrigger>
        </TabsList>

        {/* Applications Tab */}
        <TabsContent value="applications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5" />
                <span>{tDb('vendor.applications', 'Candidatures')}</span>
              </CardTitle>
              <CardDescription>
                {tDb('vendor.review_applications', 'Examinez et approuvez les nouvelles demandes de vendeurs')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{tDb('vendor.columns.candidate', 'Candidat')}</TableHead>
                    <TableHead>{tDb('vendor.columns.company', 'Entreprise')}</TableHead>
                    <TableHead>{tDb('vendor.columns.type', 'Type')}</TableHead>
                    <TableHead>{tDb('vendor.columns.status', 'Statut')}</TableHead>
                    <TableHead>{tDb('date', 'Date')}</TableHead>
                    <TableHead>{tDb('vendor.columns.actions', 'Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications.map((application) => (
                    <TableRow key={application.id}>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                            <Users className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{application.profile?.full_name || 'N/A'}</p>
                            <p className="text-sm text-muted-foreground">{application.profile?.phone}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{application.business_name}</p>
                          <p className="text-sm text-muted-foreground">{application.business_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{application.business_type}</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(application.status)}</TableCell>
                      <TableCell>{new Date(application.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedApplication(application);
                            setApplicationDialog(true);
                          }}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          {tDb('vendor.review.open', 'Examiner')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendors Tab */}
        <TabsContent value="vendors" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-blue-500 rounded-xl">
                    <Store className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{tDb('vendor.cards.active', 'Vendeurs actifs')}</p>
                    <p className="text-2xl font-bold">{vendors.filter(v => v.vendor_status === 'approved').length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-orange-500 rounded-xl">
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{tDb('vendor.cards.pending', 'En attente')}</p>
                    <p className="text-2xl font-bold">{applications.filter(a => a.status === 'pending').length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-green-500 rounded-xl">
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{tDb('vendor.cards.revenue', 'Revenus totaux')}</p>
                    <p className="text-2xl font-bold">
                      {formatPriceWithCurrency(
                        vendors.reduce((sum, v) => {
                          const revenueInCents = Math.round((v.total_revenue || 0) * 100);
                          const convertedRevenue = convertPrice(revenueInCents, v.total_revenue_currency || 'CDF', displayCurrency);
                          return sum + convertedRevenue;
                        }, 0),
                        displayCurrency,
                        displayCurrency,
                        convertPrice
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-purple-500 rounded-xl">
                    <Package className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{tDb('vendor.cards.total_products', 'Produits totaux')}</p>
                    <p className="text-2xl font-bold">{vendors.reduce((sum, v) => sum + (v.products_count || 0), 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-2">
                  <Store className="h-5 w-5" />
                  <span>{tDb('vendor.list.title', 'Liste des vendeurs')}</span>
                </CardTitle>
                <Button onClick={() => setAddVendorDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  {tDb('vendor.add_vendor', 'Ajouter un vendeur')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{tDb('vendor.columns.vendor', 'Vendeur')}</TableHead>
                    <TableHead>{tDb('vendor.columns.type', 'Type')}</TableHead>
                    <TableHead>{tDb('vendor.columns.products', 'Produits')}</TableHead>
                    <TableHead>{tDb('vendor.columns.orders', 'Commandes')}</TableHead>
                    <TableHead>{tDb('vendor.columns.revenue', 'Revenus')}</TableHead>
                    <TableHead>{tDb('vendor.columns.rating', 'Note')}</TableHead>
                    <TableHead>{tDb('vendor.columns.status', 'Statut')}</TableHead>
                    <TableHead>{tDb('vendor.columns.actions', 'Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendors.map((vendor) => (
                    <TableRow key={vendor.id}>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-primary/20 to-primary/10 rounded-full flex items-center justify-center border">
                            <Store className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{vendor.display_name || vendor.business_name || vendor.full_name}</p>
                            <p className="text-sm text-muted-foreground">{vendor.business_name}</p>
                            {vendor.is_internal && vendor.employee && (
                              <p className="text-xs text-muted-foreground">{tDb('vendor.position', 'Poste')}: {vendor.employee.position}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={vendor.is_internal ? "default" : "secondary"}>
                          {vendor.is_internal ? `${tDb('vendor.type.internal', 'Interne')} ${vendor.employee ? `(${vendor.employee.position})` : ''}` : tDb('vendor.type.external', 'Externe')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <Package className="h-4 w-4 text-muted-foreground" />
                          <span>{vendor.products_count || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>{vendor.orders_count || 0}</TableCell>
                      <TableCell className="font-mono">
                        {formatPriceWithCurrency(
                          Math.round((vendor.total_revenue || 0) * 100),
                          vendor.total_revenue_currency || 'CDF',
                          displayCurrency,
                          convertPrice
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <StarIcon className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                          <span>{vendor.rating?.toFixed(1) || 'N/A'}</span>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(vendor.vendor_status || 'pending')}</TableCell>
                       <TableCell>
                         <div className="flex items-center space-x-2">
                           <Button
                             variant="default"
                             size="sm"
                             onClick={() => {
                               setSelectedVendor(vendor);
                               initializeEditForm(vendor);
                               setVendorDialog(true);
                             }}
                           >
                             <Edit className="h-4 w-4 mr-1" />
                             Éditer
                           </Button>
                           {applications.find(app => app.user_id === vendor.id) && (
                             <Button
                               variant="outline"
                               size="sm"
                               onClick={() => {
                                 const app = applications.find(a => a.user_id === vendor.id);
                                 if (app) {
                                   setSelectedApplication(app);
                                   setApplicationDialog(true);
                                 }
                               }}
                               title="Voir la candidature originale"
                             >
                               <FileText className="h-4 w-4 mr-1" />
                               Candidature
                             </Button>
                           )}
                           <Select
                             value={vendor.vendor_status || 'pending'}
                             onValueChange={(value: 'pending' | 'approved' | 'rejected') => handleVendorStatusChange(vendor.id, value)}
                           >
                             <SelectTrigger className="w-24 h-8">
                               <SelectValue />
                             </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="approved">✓ {tDb('approved', 'Approuvé')}</SelectItem>
                                <SelectItem value="pending">⏱ {tDb('pending', 'En attente')}</SelectItem>
                                <SelectItem value="rejected">✗ {tDb('rejected', 'Rejeté')}</SelectItem>
                              </SelectContent>
                           </Select>
                         </div>
                       </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Finances Tab */}
        <TabsContent value="finances" className="space-y-6">
          <Tabs value={financeTab} onValueChange={setFinanceTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 bg-white/80 backdrop-blur-sm shadow-lg rounded-xl p-1">
              <TabsTrigger value="overview" className="flex items-center space-x-2">
                <TrendingUp className="h-4 w-4" />
                <span>Vue d'ensemble</span>
              </TabsTrigger>
              <TabsTrigger value="withdrawals" className="flex items-center space-x-2">
                <DollarSign className="h-4 w-4" />
                <span>Demandes de Retrait</span>
              </TabsTrigger>
              <TabsTrigger value="commissions" className="flex items-center space-x-2">
                <Wallet className="h-4 w-4" />
                <span>Wallet Commission</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <DollarSign className="h-5 w-5" />
                    <span>Finances des Vendeurs</span>
                  </CardTitle>
                  <CardDescription>
                    Vue d'ensemble des finances de tous les vendeurs
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <AdminVendorFinances />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="withdrawals" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <DollarSign className="h-5 w-5" />
                    <span>Gestion des Retraits</span>
                  </CardTitle>
                  <CardDescription>
                    Gérer les demandes de retrait des vendeurs
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <AdminWithdrawalManager />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="commissions" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Wallet className="h-5 w-5" />
                    <span>Wallet des Commissions</span>
                  </CardTitle>
                  <CardDescription>
                    Suivre les commissions plateforme et effectuer des retraits admin
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <AdminCommissionWalletManager />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="products" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Package className="h-5 w-5" />
                <span>{tDb('vendor.products.list_title', 'Tous les produits')}</span>
              </CardTitle>
              <CardDescription>
                {tDb('vendor.products.list_desc', 'Gérez tous les produits des vendeurs')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{tDb('vendor.products.columns.product', 'Produit')}</TableHead>
                    <TableHead>{tDb('vendor.products.columns.vendor', 'Vendeur')}</TableHead>
                    <TableHead>{tDb('vendor.products.columns.price', 'Prix')}</TableHead>
                    <TableHead>{tDb('vendor.products.columns.stock', 'Stock')}</TableHead>
                    <TableHead>{tDb('vendor.products.columns.status', 'Statut')}</TableHead>
                    <TableHead>{tDb('vendor.products.columns.created_at', 'Date de création')}</TableHead>
                    <TableHead>{tDb('vendor.columns.actions', 'Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          {product.images?.[0] ? (
                            <img
                              src={product.images[0]}
                              alt={product.title}
                              className="w-12 h-12 object-cover rounded"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center">
                              <Package className="h-5 w-5 text-primary" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{product.title}</p>
                            <p className="text-sm text-muted-foreground">
                              {product.brand || 'Sans marque'}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{product.vendor?.business_name || product.vendor?.full_name || 'N/A'}</p>
                          <p className="text-xs text-muted-foreground">{product.vendor?.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono">
                        {formatPriceWithCurrency(
                          product.price_cents,
                          product.currency,
                          displayCurrency,
                          convertPrice
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            product.stock <= 5
                              ? 'destructive'
                              : product.stock <= 10
                              ? 'secondary'
                              : 'outline'
                          }
                        >
                          {product.stock} {product.stock > 1 ? 'unités' : 'unité'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={product.active ? "default" : "secondary"}>
                          {product.active ? tDb('active', 'Actif') : tDb('inactive', 'Inactif')}
                        </Badge>
                      </TableCell>
                      <TableCell>{new Date(product.created_at).toLocaleDateString('fr-FR')}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEditProduct(product)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => window.open(`/product/${product.id}`, '_blank')}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteProduct(product.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>{tDb('vendor.analytics.performance', 'Performance des vendeurs')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {vendors
                    .sort((a, b) => (b.total_revenue || 0) - (a.total_revenue || 0))
                    .slice(0, 5)
                    .map((vendor, index) => (
                      <div key={vendor.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="text-sm font-bold text-muted-foreground">#{index + 1}</div>
                          <div>
                            <p className="font-medium">{vendor.business_name || vendor.full_name}</p>
                            <p className="text-sm text-muted-foreground">{vendor.products_count} produits</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold">
                            {formatPriceWithCurrency(
                              Math.round((vendor.total_revenue || 0) * 100),
                              vendor.total_revenue_currency || 'CDF',
                              displayCurrency,
                              convertPrice
                            )}
                          </p>
                          <p className="text-sm text-muted-foreground">{vendor.orders_count} commandes</p>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{tDb('vendor.analytics.popular_categories', 'Catégories populaires')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center text-muted-foreground py-8">
                    <TrendingUp className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>{tDb('vendor.analytics.placeholder', 'Données analytiques bientôt disponibles')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Managers Tab */}
        <TabsContent value="managers" className="space-y-6">
          <VendorManagersTab />
        </TabsContent>
      </Tabs>

      {/* Application Review Dialog */}
      <Dialog open={applicationDialog} onOpenChange={setApplicationDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{tDb('vendor.review.title', 'Examiner la candidature')}</DialogTitle>
            <DialogDescription>
              {tDb('vendor.review.desc', 'Examinez les informations et approuvez ou rejetez cette candidature')}
            </DialogDescription>
          </DialogHeader>
          
          {selectedApplication && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">{tDb('vendor.review.labels.candidate', 'Candidat')}</Label>
                  <p className="text-lg font-semibold">{selectedApplication.profile?.full_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedApplication.profile?.phone}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">{tDb('vendor.review.labels.current_status', 'Statut actuel')}</Label>
                  <div className="mt-1">{getStatusBadge(selectedApplication.status)}</div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">{tDb('vendor.review.labels.company_name', 'Nom de l\'entreprise')}</Label>
                  <p className="mt-1">{selectedApplication.business_name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">{tDb('vendor.review.labels.company_type', 'Type d\'entreprise')}</Label>
                  <p className="mt-1">{selectedApplication.business_type}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">{tDb('vendor.review.labels.description', 'Description')}</Label>
                  <p className="mt-1">{selectedApplication.business_description || tDb('none', 'Aucune')}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">{tDb('vendor.review.labels.email', 'Email')}</Label>
                    <p className="mt-1">{selectedApplication.business_email}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">{tDb('vendor.review.labels.phone', 'Téléphone')}</Label>
                    <p className="mt-1">{selectedApplication.business_phone}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">{tDb('vendor.review.labels.address', 'Adresse')}</Label>
                  <p className="mt-1">{selectedApplication.business_address}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">{tDb('vendor.review.labels.experience', 'Expérience')}</Label>
                  <p className="mt-1">{selectedApplication.experience || tDb('unspecified', 'Non spécifié')}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">{tDb('vendor.review.labels.motivation', 'Motivation')}</Label>
                  <p className="mt-1">{selectedApplication.motivation || tDb('unspecified', 'Non spécifié')}</p>
                </div>
                {selectedApplication.identity_document_url && (
                  <div>
                    <Label className="text-sm font-medium">{tDb('vendor.review.labels.identity_document', 'Document d\'identité')}</Label>
                    <div className="mt-2">
                      <Button
                        variant="link"
                        className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800 p-0 h-auto"
                        onClick={async () => {
                          try {
                            const docUrl = selectedApplication.identity_document_url!;
                            
                            // Si c'est déjà une URL complète (ancien format), l'ouvrir directement
                            if (docUrl.startsWith('http')) {
                              window.open(docUrl, '_blank');
                              return;
                            }
                            
                            // Sinon, générer une URL signée pour le bucket privé
                            const { data, error } = await supabase.storage
                              .from('vendor-documents')
                              .createSignedUrl(docUrl, 3600); // 1 heure
                            
                            if (error) throw error;
                            if (data?.signedUrl) {
                              window.open(data.signedUrl, '_blank');
                            }
                          } catch (err) {
                            console.error('Erreur accès document:', err);
                            toast({
                              title: 'Erreur',
                              description: 'Impossible d\'accéder au document',
                              variant: 'destructive'
                            });
                          }
                        }}
                      >
                        <FileText className="h-4 w-4" />
                        <span>Voir le document</span>
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {selectedApplication.status === 'pending' && (
                <div className="space-y-4">
                  <div>
                  <Label htmlFor="admin_notes">{tDb('vendor.review.labels.admin_notes', 'Notes de l\'administrateur')}</Label>
                  <Textarea
                    id="admin_notes"
                    value={reviewForm.admin_notes}
                    onChange={(e) => setReviewForm(prev => ({ ...prev, admin_notes: e.target.value }))}
                    placeholder={tDb('write_comment', 'Écrire un commentaire')}
                    className="mt-1"
                  />
                  </div>
                  <div className="flex space-x-3">
                    <Button
                      onClick={() => handleReviewApplication(selectedApplication, 'approved', reviewForm.admin_notes)}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approuver
                    </Button>
                    <Button
                      onClick={() => handleReviewApplication(selectedApplication, 'rejected', reviewForm.admin_notes)}
                      variant="destructive"
                      className="flex-1"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Rejeter
                    </Button>
                  </div>
                </div>
              )}

              {/* Bouton supprimer (super_admin uniquement) */}
              {user?.system_role === 'super_admin' && (
                <div className="pt-4 border-t">
                  <Button
                    onClick={() => handleDeleteApplication(selectedApplication)}
                    variant="outline"
                    className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Supprimer cette candidature
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Vendor Edit Dialog */}
      <Dialog open={vendorDialog} onOpenChange={setVendorDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gestion du vendeur - {selectedVendor?.full_name || selectedVendor?.business_name}</DialogTitle>
            <DialogDescription>
              Informations, finances et statistiques du vendeur
            </DialogDescription>
          </DialogHeader>
          
          {selectedVendor && (
            <Tabs value={vendorDetailsTab} onValueChange={setVendorDetailsTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="info">
                  <UserCheck className="h-4 w-4 mr-2" />
                  Informations
                </TabsTrigger>
                <TabsTrigger value="finances">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Finances
                </TabsTrigger>
                <TabsTrigger value="stats">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Statistiques
                </TabsTrigger>
              </TabsList>

              {/* Info Tab */}
              <TabsContent value="info" className="space-y-6 mt-6">
              {/* Informations de base */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="full_name">Nom complet</Label>
                  <Input
                    id="full_name"
                    value={editVendorForm.full_name}
                    onChange={(e) => setEditVendorForm(prev => ({ ...prev, full_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="business_name">Nom de l'entreprise</Label>
                  <Input
                    id="business_name"
                    value={editVendorForm.business_name}
                    onChange={(e) => setEditVendorForm(prev => ({ ...prev, business_name: e.target.value }))}
                  />
                </div>
              </div>

              {/* Type de vendeur */}
              <div>
                <Label htmlFor="vendor_type">Type de vendeur</Label>
                <Select
                  value={editVendorForm.is_internal ? 'internal' : 'external'}
                  onValueChange={(value) => setEditVendorForm(prev => ({ ...prev, is_internal: value === 'internal' }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="external">
                      <div className="flex items-center space-x-2">
                        <UserCheck className="h-4 w-4" />
                        <span>{tDb('vendor.type.external', 'Externe')}</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="internal">
                      <div className="flex items-center space-x-2">
                        <Building2 className="h-4 w-4" />
                        <span>{tDb('vendor.type.internal', 'Interne')}</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Mode Test du Stock */}
              <div className="border rounded-lg p-4 bg-muted/50">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Mode Test du Stock
                  </Label>
                  <Badge variant={editVendorForm.stock_test_mode === null ? 'secondary' : editVendorForm.stock_test_mode ? 'default' : 'outline'}>
                    {editVendorForm.stock_test_mode === null ? 'Global' : editVendorForm.stock_test_mode ? 'Test' : 'Production'}
                  </Badge>
                </div>
                <Select
                  value={editVendorForm.stock_test_mode === null ? 'global' : editVendorForm.stock_test_mode ? 'test' : 'production'}
                  onValueChange={(value) => {
                    const newValue = value === 'global' ? null : value === 'test';
                    setEditVendorForm(prev => ({ ...prev, stock_test_mode: newValue }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">
                      <div className="flex items-center space-x-2">
                        <Globe className="h-4 w-4" />
                        <span>Utiliser la configuration globale</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="test">
                      <div className="flex items-center space-x-2">
                        <TestTube className="h-4 w-4 text-amber-500" />
                        <span>Mode Test (stock virtuel illimité)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="production">
                      <div className="flex items-center space-x-2">
                        <Shield className="h-4 w-4 text-green-500" />
                        <span>Mode Production (validation stricte)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-2">
                  Configure le mode de gestion du stock pour ce vendeur. La configuration individuelle est prioritaire sur la configuration globale.
                </p>
              </div>

              {/* Contact */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    value={editVendorForm.phone}
                    onChange={(e) => setEditVendorForm(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="business_email">Email professionnel</Label>
                  <Input
                    id="business_email"
                    type="email"
                    value={editVendorForm.business_email}
                    onChange={(e) => setEditVendorForm(prev => ({ ...prev, business_email: e.target.value }))}
                  />
                </div>
              </div>

              {/* Adresse */}
              <div>
                <Label htmlFor="business_address">Adresse de l'entreprise</Label>
                <Textarea
                  id="business_address"
                  value={editVendorForm.business_address}
                  onChange={(e) => setEditVendorForm(prev => ({ ...prev, business_address: e.target.value }))}
                  placeholder="Adresse complète de l'entreprise"
                />
              </div>

              {/* Heures d'ouverture */}
              <div>
                <Label className="text-sm md:text-base font-medium">Heures d'ouverture</Label>
                <div className="grid grid-cols-1 gap-2 md:gap-3 mt-3">
                  {Object.entries(editVendorForm.business_hours).map(([day, hours]) => (
                    <div key={day} className="flex items-center gap-2 md:space-x-4 p-2 md:p-3 border rounded-lg">
                      <div className="w-16 md:w-20 text-xs md:text-sm font-medium capitalize flex-shrink-0">
                        {day === 'monday' ? 'Lundi' :
                         day === 'tuesday' ? 'Mardi' :
                         day === 'wednesday' ? 'Mercredi' :
                         day === 'thursday' ? 'Jeudi' :
                         day === 'friday' ? 'Vendredi' :
                         day === 'saturday' ? 'Samedi' :
                         'Dimanche'}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Input
                          type="time"
                          value={hours.open}
                          onChange={(e) => setEditVendorForm(prev => ({
                            ...prev,
                            business_hours: {
                              ...prev.business_hours,
                              [day]: { ...hours, open: e.target.value }
                            }
                          }))}
                          className="w-24"
                          disabled={hours.closed}
                        />
                        <span className="text-sm text-muted-foreground">à</span>
                        <Input
                          type="time"
                          value={hours.close}
                          onChange={(e) => setEditVendorForm(prev => ({
                            ...prev,
                            business_hours: {
                              ...prev.business_hours,
                              [day]: { ...hours, close: e.target.value }
                            }
                          }))}
                          className="w-24"
                          disabled={hours.closed}
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={hours.closed}
                          onChange={(e) => setEditVendorForm(prev => ({
                            ...prev,
                            business_hours: {
                              ...prev.business_hours,
                              [day]: { ...hours, closed: e.target.checked }
                            }
                          }))}
                          className="rounded"
                        />
                        <span className="text-sm text-muted-foreground">Fermé</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              </TabsContent>

              {/* Finances Tab */}
              <TabsContent value="finances" className="mt-6">
                <VendorFinanceDetails 
                  vendorId={selectedVendor.id} 
                  vendorName={selectedVendor.full_name || selectedVendor.business_name || ''} 
                />
              </TabsContent>

              {/* Stats Tab */}
              <TabsContent value="stats" className="mt-6">
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription className="flex items-center">
                          <Package className="h-4 w-4 mr-2 text-blue-500" />
                          Produits
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-blue-600">
                          {selectedVendor.products_count || 0}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription className="flex items-center">
                          <ShoppingCart className="h-4 w-4 mr-2 text-purple-500" />
                          Commandes
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-purple-600">
                          {selectedVendor.orders_count || 0}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription className="flex items-center">
                          <DollarSign className="h-4 w-4 mr-2 text-green-500" />
                          Revenus totaux
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-green-600">
                          {formatPriceWithCurrency(
                            Math.round((selectedVendor.total_revenue || 0) * 100),
                            selectedVendor.total_revenue_currency || 'CDF',
                            displayCurrency,
                            convertPrice
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription className="flex items-center">
                          <StarIcon className="h-4 w-4 mr-2 text-yellow-500 fill-current" />
                          Note moyenne
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-yellow-600">
                          {selectedVendor.rating?.toFixed(1) || 'N/A'}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle>Informations commerciales</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between items-center border-b pb-2">
                        <span className="text-muted-foreground">Type de vendeur</span>
                        <Badge variant={selectedVendor.is_internal ? "default" : "secondary"}>
                          {selectedVendor.is_internal ? 'Interne' : 'Externe'}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center border-b pb-2">
                        <span className="text-muted-foreground">Statut</span>
                        {getStatusBadge(selectedVendor.vendor_status || 'pending')}
                      </div>
                      <div className="flex justify-between items-center border-b pb-2">
                        <span className="text-muted-foreground">Date d'inscription</span>
                        <span className="font-medium">
                          {new Date(selectedVendor.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                      {selectedVendor.phone && (
                        <div className="flex justify-between items-center border-b pb-2">
                          <span className="text-muted-foreground">Téléphone</span>
                          <span className="font-medium">{selectedVendor.phone}</span>
                        </div>
                      )}
                      {selectedVendor.business_email && (
                        <div className="flex justify-between items-center border-b pb-2">
                          <span className="text-muted-foreground">Email</span>
                          <span className="font-medium">{selectedVendor.business_email}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setVendorDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleUpdateVendor}>
              <Edit className="h-4 w-4 mr-2" />
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={productDialog} onOpenChange={setProductDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le produit</DialogTitle>
            <DialogDescription>
              Modifiez les informations du produit
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Titre</Label>
              <Input
                value={productForm.title}
                onChange={(e) =>
                  setProductForm({ ...productForm, title: e.target.value })
                }
              />
            </div>

            <div>
              <Label>Description</Label>
              <WysiwygEditor
                value={productForm.description}
                onChange={(value) =>
                  setProductForm({ ...productForm, description: value })
                }
                placeholder="Description du produit..."
                className="min-h-[200px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prix (en centimes)</Label>
                <Input
                  type="number"
                  value={productForm.price_cents}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      price_cents: parseInt(e.target.value) || 0,
                    })
                  }
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {formatPriceWithCurrency(
                    productForm.price_cents,
                    productForm.currency,
                    displayCurrency,
                    convertPrice
                  )}
                </p>
              </div>

              <div>
                <Label>Devise</Label>
                <Select
                  value={productForm.currency}
                  onValueChange={(value) =>
                    setProductForm({ ...productForm, currency: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CDF">CDF - Franc Congolais</SelectItem>
                    <SelectItem value="USD">USD - Dollar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Stock</Label>
                <Input
                  type="number"
                  value={productForm.stock}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      stock: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>

              <div>
                <Label>Marque</Label>
                <Input
                  value={productForm.brand}
                  onChange={(e) =>
                    setProductForm({ ...productForm, brand: e.target.value })
                  }
                />
              </div>
            </div>

            <div>
              <Label>Catégorie</Label>
              <Input
                value={productForm.category}
                onChange={(e) =>
                  setProductForm({ ...productForm, category: e.target.value })
                }
              />
            </div>

            <div>
              <Label>Images du produit</Label>
              <ProductImageUpload
                images={productForm.images}
                onImagesChange={(images) =>
                  setProductForm({ ...productForm, images })
                }
                maxImages={5}
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="active"
                checked={productForm.active}
                onCheckedChange={(checked) =>
                  setProductForm({ ...productForm, active: checked })
                }
              />
              <Label htmlFor="active">Produit actif</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setProductDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveProduct}>
              <Edit className="h-4 w-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Vendor Form */}
      <AddVendorForm
        open={addVendorDialog}
        onOpenChange={setAddVendorDialog}
        onSuccess={fetchAllData}
      />
    </div>
  );
};